<template>
  <SlideIn
    :open="isOpen"
    :heading="`Your Bag (${lineItemsCount})`"
    @close="isOpen = false"
  >
    <template #content>
      <FreeShippingProgress :subtotal="subtotal" />
      <CartWarnings />

      <template v-if="lines.length > 0">
        <ul>
          <li v-for="line in lines" :key="line.id">
            <CartLine
              :line="line"
              class="cart-line"
              @removeLine="handleRemoveCartLine(line)"
              @updateLine="(val) => handleUpdateCartLine(line, val)"
            />
          </li>
        </ul>

        <NostoProductRecommendationsGrid
          :recommendations="nostoProductRecommendationsProduct"
          :placement="nostoProductRecommendationsProductId"
          overflow
          heading="You might also like"
        />
        <NostoProductRecommendationsGrid
          :recommendations="nostoProductRecommendationsBehaviour"
          :placement="nostoProductRecommendationsBehaviourId"
          overflow
          heading="An edit just for you"
        />
      </template>

      <EmptyBagState v-else />
    </template>
    <template v-if="showFooter" #footer>
      <div class="button-group">
        <div class="cart-price">
          <span class="cart-price__heading">Subtotal</span>
          <span class="cart-price__value">
            {{ $filters.toCurrency(subtotal.amount, subtotal.currencyCode) }}
          </span>
        </div>
        <Button tag="a" :href="checkoutUrl" full-width> Go to checkout </Button>
      </div>

      <IconText v-if="isEDDEnabled" icon="shipping">
        <DeliveryEstimatorNoInput class="estimate-delivery-date" />
      </IconText>
      <IconText icon="returns"> 30 Day Returns On All Items </IconText>

      <PaymentMethods :price="subtotal" />
    </template>
  </SlideIn>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";

import Button from "@/components/Button.vue";
import CartLine from "@/components/cart/CartLine.vue";
import IconText from "@/components/IconText.vue";
import PaymentMethods from "@/components/cart/PaymentMethods.vue";
import NostoProductRecommendationsGrid from "@/components/NostoProductRecommendationsGrid.vue";
import SlideIn from "@/components/SlideIn.vue";
import DeliveryEstimatorNoInput from "@/components/product-display/DeliveryEstimatorNoInput.vue";
import FreeShippingProgress from "@/components/cart/FreeShippingProgress.vue";
import EmptyBagState from "@/components/cart/EmptyBagState.vue";
import { regionFeatureToggles } from "@/config.js";
import CartWarnings from "@/components/cart/CartWarnings.vue";

export default {
  name: "BagV2",
  components: {
    CartLine,
    SlideIn,
    Button,
    NostoProductRecommendationsGrid,
    DeliveryEstimatorNoInput,
    IconText,
    PaymentMethods,
    FreeShippingProgress,
    EmptyBagState,
    CartWarnings,
  },
  emits: ["update:open"],
  data() {
    return {
      nostoProductRecommendationsProduct: null,
      nostoProductRecommendationsBehaviour: null,
      nostoProductRecommendationsProductId: null,
      nostoProductRecommendationsBehaviourId: null,
    };
  },
  computed: {
    ...mapState("cart", ["cart", "isCartOpen", "multipassCheckoutUrl"]),
    ...mapGetters("cart", ["lines", "subtotal", "lineItemsCount"]),
    ...mapGetters("app", ["currencyCode"]),
    isOpen: {
      get() {
        return this.isCartOpen;
      },
      set(val) {
        return this.setCartOpen(val);
      },
    },
    showFooter() {
      return this.lines.length > 0;
    },
    checkoutUrl() {
      if (this.multipassCheckoutUrl) {
        return this.multipassCheckoutUrl;
      }

      return this.cart.checkoutUrl;
    },
    isEDDEnabled() {
      if (this.currencyCode) {
        return regionFeatureToggles[this.currencyCode].eddEnabled || false;
      }
      return false;
    },
  },
  watch: {
    isOpen(val) {
      if (val) {
        this.sendNosto();
      }
    },
  },
  methods: {
    ...mapActions("cart", [
      "removeCartItem",
      "updateCartItem",
      "setCartOpen",
      "checkCartRules",
    ]),
    async handleRemoveCartLine({ id }) {
      try {
        await this.removeCartItem(id);
      } catch (error) {
        console.log(error);
      }

      if (this.lineItemsCount === 0) {
        this.setCartOpen(false);
      }
    },
    async handleUpdateCartLine(line, newQuantity) {
      if (newQuantity > line.quantity) {
        const cartRuleResults = await this.checkCartRules({
          productId: line.merchandise.product.id,
        });
        if (!cartRuleResults) {
          return;
        }
      }

      try {
        await this.updateCartItem({
          id: line.id,
          quantity: newQuantity,
          merchandiseId: line.merchandise.id,
        });
      } catch (error) {
        console.log(error);
      }
    },
    sendNosto() {
      // eslint-disable-next-line no-undef
      nostojs((api) => {
        api
          .defaultSession()
          .viewCart()
          .setPlacements(["cartpage-nosto-1", "cartpage-nosto-2"])
          .load()
          .then((data) => {
            console.log(data.recommendations["cartpage-nosto-1"]);
            console.log(data.recommendations["cartpage-nosto-2"]);
            this.nostoProductRecommendationsProduct =
              data.recommendations["cartpage-nosto-1"]?.products || null;
            this.nostoProductRecommendationsProductId =
              data.recommendations["cartpage-nosto-1"]?.result_id || null;
            this.nostoProductRecommendationsBehaviour =
              data.recommendations["cartpage-nosto-2"]?.products || null;
            this.nostoProductRecommendationsBehaviourId =
              data.recommendations["cartpage-nosto-2"]?.result_id || null;
          });
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.cart-line {
  border-bottom: 1px solid rgba($color-grey-dark, 0.2);
  margin: -20px;
  padding: 20px;

  &:last-child {
    border-bottom: none;
  }
}

.cart-price {
  display: flex;
  justify-content: space-between;
  @include t__utility-label-30;
  margin-bottom: 20px;
}

.button-group {
  margin-bottom: 10px;
  :deep(.button + .button) {
    margin-top: 10px;
  }
}

.estimate-delivery-date {
  text-align: left;
  margin-top: 0;

  :deep(.label) {
    display: block;
    text-transform: none;
    font-weight: 400;
    margin-bottom: 4px;
  }
}
</style>
